/**
 * Community designs — the parts that are safe to import anywhere, including client components.
 * Database access lives in `./server.ts` (service role; server only).
 *
 * Banners, shields and skins. Signs and books are excluded on purpose: a published sign could hide a
 * `run_command` click action that no preview shows.
 *
 * ── ADDING A DESIGN TYPE (custom mobs, …) — every step is required ──────────────────────────────
 * 1. Add it to DESIGN_TYPE_LIST and give it a row in DESIGN_TYPES below.
 * 2. `types/<family>.ts` — the client adapter (thumbnail, open in editor, save a copy); register it in
 *    `types/index.ts`.
 * 3. `types/<family>.server.ts` — the server adapter (which saved build it publishes from, and the
 *    SAFETY CHECK); register it in `types/server.ts`.
 *    Both registries are Record<DesignType, …>, so the build fails until 2 and 3 exist.
 * 4. A migration widening the `community_designs.type` CHECK constraint.
 * 5. An og:image renderer for the tool in OG_REGISTRY, or its designs unfurl as the generic tool card.
 * 6. A case in `tests/community-designs.spec.ts` publishing one, and a refused-state case for step 3.
 * Optional: `Preview` / `prepareOpen` / `download` on the client adapter (skins use all three), and
 * `versionless` / `moderatorNote` on the registry row below.
 *
 * ⚠️ Step 3 is the one that matters. Banners are safe because, with custom name and lore refused, their
 * state is a bounded list of colors and patterns. Anything carrying entity or item NBT can carry a
 * command (a command-block minecart as a passenger, a spawner, a written book in a hand slot, a click
 * event in a name). ALLOW-LIST the fields a type may contain; never block-list the dangerous ones.
 */

export const DESIGN_TYPE_LIST = ['banner', 'shield', 'skin'] as const;
export type DesignType = (typeof DESIGN_TYPE_LIST)[number];
export type DesignStatus = 'pending' | 'approved' | 'removed';
export type DesignSort = 'new' | 'trending' | 'liked' | 'copied';

export interface DesignTypeInfo {
  /** URL segment: /designs/<slug>. Plural, because that is what people search for. */
  slug: string;
  /** Tool slug in toolsCatalog / OG_REGISTRY. */
  tool: string;
  editorName: string;
  /** The command the editor produces, for page copy ("get the /give command"). Null for skins (a file, not a command). */
  command: string | null;
  /** What opening it in the editor gets you, for page copy: "open it in the Banner Editor to <outcome>". */
  outcome: string;
  /** Not tied to a game version (skins): published with no "Made for" version. */
  versionless?: boolean;
  /** What a moderator must check by eye because no automated check can — shown in the admin queue and on the design page. */
  moderatorNote?: string;
  singular: string;
  plural: string;
  /** `LibraryItem.type` of this build in My Library — where "Publish to community" is offered. */
  libraryType: string;
  /** What this type refuses at publish, shown in the publish panel. */
  publishNote: string;
}

const BANNER_NOTE = 'Designs with a custom name or lore cannot be published.';
const SKIN_NOTE = 'Only publish a skin you made — not another player’s. Skins loaded by username cannot be published.';

export const DESIGN_TYPES: Record<DesignType, DesignTypeInfo> = {
  banner: {
    slug: 'banners', tool: 'banner-editor', editorName: 'Banner Editor', command: '/give', outcome: 'get the /give command',
    singular: 'banner', plural: 'banners', libraryType: 'banner', publishNote: BANNER_NOTE,
  },
  shield: {
    slug: 'shields', tool: 'shield-editor', editorName: 'Shield Editor', command: '/give', outcome: 'get the /give command',
    singular: 'shield', plural: 'shields', libraryType: 'shield', publishNote: BANNER_NOTE,
  },
  // Saved as `saved_elements.type = 'skin'` (useSavedElements.saveSkin). State: `<model>:<base64 PNG>` —
  // see app/lib/community/skinLayout.ts; the safety check re-encodes every pixel on the server.
  skin: {
    slug: 'skins', tool: 'skin-editor', editorName: 'Skin Editor', command: null, outcome: 'download the skin file',
    singular: 'skin', plural: 'skins', libraryType: 'skin', publishNote: SKIN_NOTE, versionless: true,
    moderatorNote: 'Check it isn’t another player’s skin re-posted — the editor loads anyone’s skin by username, and the site cannot tell. Look at every face in the skin file, not just the front.',
  },
};

/** `/designs/<slug>` segment to type. */
export function designTypeFromSlug(slug: string): DesignType | null {
  for (const t of DESIGN_TYPE_LIST) if (DESIGN_TYPES[t].slug === slug) return t;
  return null;
}

/** The design type a My Library item publishes as, or null when that kind of build cannot be published. */
export function designTypeForLibraryType(libraryType: string): DesignType | null {
  for (const t of DESIGN_TYPE_LIST) if (DESIGN_TYPES[t].libraryType === libraryType) return t;
  return null;
}

/** Object.hasOwn so `constructor` etc. can never resolve. */
export function isDesignType(value: unknown): value is DesignType {
  return typeof value === 'string' && Object.hasOwn(DESIGN_TYPES, value);
}

/** Same shape as share ids (8 chars base62). Checked before any database read. */
export const DESIGN_ID_PATTERN = /^[A-Za-z0-9]{8}$/;

/** Same bound as /api/share: a version id is short and dotted. */
export const VERSION_RE = /^[0-9]{1,4}(\.[0-9]{1,4}){0,3}$/;

export const TITLE_MIN = 3;
export const TITLE_MAX = 60;
export const PAGE_SIZE = 24;
/**
 * A design type's pages stay `noindex` (and out of the sitemap) until it has this many approved
 * designs — one full listing page. Indexed early, a near-empty gallery is judged thin against
 * galleries with thousands of designs, and that first impression is slow to undo. It switches on by
 * itself when the count is reached; no date, no deploy.
 */
export const INDEX_MIN_DESIGNS = PAGE_SIZE;
/** Distinct reports that hide an approved design until an admin reviews it. */
export const REPORT_HIDE_THRESHOLD = 3;
/** The window "Trending" counts likes and copies in. */
export const TRENDING_DAYS = 7;

/** Listing orders, in tab order. Only `new` is ever indexed; the others are the same designs re-ordered. */
export const DESIGN_SORTS: readonly { key: DesignSort; label: string }[] = [
  { key: 'new', label: 'Newest' },
  { key: 'trending', label: 'Trending' },
  { key: 'liked', label: 'Most liked' },
  { key: 'copied', label: 'Most copied' },
];

export function parseSort(raw: unknown): DesignSort {
  return DESIGN_SORTS.some(s => s.key === raw) ? (raw as DesignSort) : 'new';
}

/**
 * Shape rules for a title, shared by the publish form (instant feedback) and the server (the real
 * check, which also runs the word filter). Letters, numbers, spaces and light punctuation only, and no
 * links — a title is a name, not a place to advertise.
 */
export function titleProblem(raw: string): string | null {
  const title = raw.trim();
  if (title.length < TITLE_MIN) return `Use at least ${TITLE_MIN} characters.`;
  if (title.length > TITLE_MAX) return `Keep it to ${TITLE_MAX} characters or fewer.`;
  if (!/^[\p{L}\p{N} '&!?.,()\-:#+]+$/u.test(title)) return 'Use letters, numbers, spaces and simple punctuation only.';
  if (/https?:|www\.|\.(com|net|org|gg|io|xyz)\b/i.test(title)) return 'Titles cannot contain links.';
  return null;
}

/** Collapse runs of spaces so "Royal   Guard" and "Royal Guard" are the same title. */
export function normaliseTitle(raw: string): string {
  // NFC first: macOS and some keyboards send an accent as a separate combining mark ("e" + U+0301), which
  // the title's \p{L} check refuses — so "Café" typed on a Mac was rejected while the same word pasted
  // from elsewhere passed.
  return raw.normalize('NFC').trim().replace(/\s+/g, ' ');
}

export function capitalize(s: string): string {
  return s ? `${s[0].toUpperCase()}${s.slice(1)}` : s;
}

/** "banners", "banners & shields", "banners, shields & mobs". */
export function joinWithAnd(words: readonly string[], conjunction = '&'): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} ${conjunction} ${words[words.length - 1]}`;
}

export function designPath(type: DesignType, id: string): string {
  return `/designs/${DESIGN_TYPES[type].slug}/${id}`;
}

export function listingPath(type: DesignType): string {
  return `/designs/${DESIGN_TYPES[type].slug}`;
}

/** Open a design in its editor, at the version it was made in when that is known. */
export function openInToolHref(type: DesignType, state: string, version: string | null): string {
  const query = version && VERSION_RE.test(version) ? `?version=${encodeURIComponent(version)}` : '';
  return `/tools/${DESIGN_TYPES[type].tool}${query}#${state}`;
}

/** What a page or client component receives — never the author's user id. */
export interface PublicDesign {
  id: string;
  type: DesignType;
  title: string;
  authorUsername: string;
  state: string;
  gameVersion: string | null;
  status: DesignStatus;
  likeCount: number;
  copyCount: number;
  viewCount: number;
  createdAt: string;
  /** When a moderator last decided on it — drives the updates dot on My Library. */
  reviewedAt: string | null;
  /** A moderator's reason for removing it, if they gave one. Null on anything not removed. */
  moderationNote: string | null;
}

/**
 * Whether an author may delete their own design. Not once a moderator REMOVED it — the removal, and the
 * reports behind it, must outlive the person it was about — and not while reports have it HIDDEN awaiting
 * review: deleting it then cascaded its reports away and let the identical design come straight back with
 * a clean record. Used by the unpublish route and to hide the button on the design page.
 */
export function canAuthorUnpublish(status: string, reportCount: number): boolean {
  if (status === 'removed') return false;
  return !(status === 'pending' && reportCount >= REPORT_HIDE_THRESHOLD);
}
